"use client";

import { useEffect, useState } from "react";
import NumberFlow from "@number-flow/react";

import { cn } from "src/utils/Functions";

export interface FlowNumberProps {
  value: number;
  prefix?: string;
  suffix?: string;
  format?: Intl.NumberFormatOptions;
  className?: string;
  /** Re-run the count-up when this identity changes. */
  replayKey?: string | number;
}

export function FlowNumber({
  value,
  prefix,
  suffix,
  format,
  className,
  replayKey,
}: FlowNumberProps) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(false);
    const frame = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(frame);
  }, [value, replayKey]);

  return (
    <NumberFlow
      value={ready ? value : 0}
      prefix={prefix}
      suffix={suffix}
      format={format ?? { maximumFractionDigits: 0 }}
      className={cn("tabular-nums", className)}
    />
  );
}

export interface StatBlockProps {
  label: string;
  value: number;
  prefix?: string;
  suffix?: string;
  format?: Intl.NumberFormatOptions;
  replayKey?: string | number;
  className?: string;
  valueClassName?: string;
  labelClassName?: string;
  align?: "left" | "center" | "right";
}

const alignClass = {
  left: "text-left items-start",
  center: "text-center items-center",
  right: "text-right items-end",
} as const;

export function StatBlock({
  label,
  value,
  prefix,
  suffix,
  format,
  replayKey,
  className,
  valueClassName,
  labelClassName,
  align = "center",
}: StatBlockProps) {
  return (
    <div className={cn("flex flex-col", alignClass[align], className)}>
      <FlowNumber
        value={value}
        prefix={prefix}
        suffix={suffix}
        format={format}
        replayKey={replayKey}
        className={cn("text-lg font-semibold", valueClassName)}
      />
      <p className={cn("text-[11px] text-muted-foreground", labelClassName)}>{label}</p>
    </div>
  );
}

